import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';

const GaSources: React.FC = () => {
  const { ga, isCustomRangePending } = useAnalyticsContext();
  const sourcesError = ga.sources.error as any;

  if (!ga.integration.isConfigured) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Acquisition Sources
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
          {ga.integration.message ||
            'Connect Google Analytics to view acquisition sources.'}
        </CardContent>
      </Card>
    );
  }

  if (sourcesError && sourcesError.status && sourcesError.status !== 400) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Acquisition Sources
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-red-600">
          {sourcesError.data?.message ||
            sourcesError.message ||
            'Failed to load acquisition data.'}
        </CardContent>
      </Card>
    );
  }

  if (ga.sources.isLoading) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Acquisition Sources
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between rounded-lg border bg-card/50 px-3 py-2"
            >
              <div className="h-4 w-32 animate-pulse rounded bg-muted/50" />
              <div className="h-4 w-16 animate-pulse rounded bg-muted/40" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isCustomRangePending) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Acquisition Sources
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
          Select a start and end date to view Google Analytics insights.
        </CardContent>
      </Card>
    );
  }

  if (!ga.sources.data.length) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Acquisition Sources
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
          No acquisition data available for this period.
        </CardContent>
      </Card>
    );
  }

  const totalSessions = ga.sources.data.reduce(
    (sum, item) => sum + item.sessions,
    0,
  );

  return (
    <Card className="border-none shadow-sm h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">
          Top Acquisition Sources
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {ga.sources.data.map(item => {
          const sessionShare =
            totalSessions > 0 ? (item.sessions / totalSessions) * 100 : 0;
          return (
            <div
              key={item.source}
              className="rounded-lg border bg-card/50 p-3"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">
                  {item.source}
                </span>
                <span className="text-sm text-muted-foreground">
                  {item.sessions.toLocaleString()} sessions
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{sessionShare.toFixed(1)}% of sessions</span>
                <span>
                  {item.newUsers.toLocaleString()} new ·{' '}
                  {item.totalUsers.toLocaleString()} total users
                </span>
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-muted">
                <div
                  className="h-2 rounded-full bg-primary"
                  style={{ width: `${Math.min(sessionShare, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default GaSources;

